import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Etichetta, Linguette, Modale, Tabella, type TonoEtichetta } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * «Configura su client di posta» (564; decisioni di Davide del 4/11/2026, docs/16-programmi-posta.md).
 *
 * Per ogni casella che la persona usa con «legge e scrive»: lo stato della sua richiesta e quello che puo' fare.
 * Senza richiesta (o rifiutata, scaduta, revocata) la stima dello spazio e «Chiedi l'accesso da un programma di
 * posta» con il motivo; in attesa le decisioni arrivate (servono la Direzione e l’amministratore di sistema (Honey et Molly)); approvata
 * l'avanzamento della copia dello storico; pronta la password, mostrata una volta con «Copia», e le istruzioni per
 * Thunderbird, Outlook, Apple Mail e iPhone, Android. Chi gestisce gli utenti, la Direzione e l’amministratore di sistema (Honey et Molly) vedono
 * anche tutte le richieste; chi gestisce gli utenti le revoca.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
const quando = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const euro = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })
const numero = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 })

const STATI: Record<posta.StatoProgramma, { testo: string; tono: TonoEtichetta }> = {
  in_attesa: { testo: 'In attesa di approvazione', tono: 'attenzione' },
  preparazione: { testo: 'Approvata, sincronizzazione in corso', tono: 'informativo' },
  pronta: { testo: 'Pronta', tono: 'positivo' },
  rifiutata: { testo: 'Rifiutata', tono: 'critico' },
  scaduta: { testo: 'Scaduta', tono: 'neutro' },
  revocata: { testo: 'Revocata', tono: 'neutro' },
}

export function testoStato(c: { stato: posta.StatoProgramma; avanzamento: number | null }): string {
  return c.stato === 'preparazione' && c.avanzamento !== null
    ? `${STATI.preparazione.testo} (${c.avanzamento}%)` : STATI[c.stato].testo
}

/** Il comando in cima alla Posta e la finestra. */
export function ConfiguraProgrammi() {
  const [aperta, setAperta] = useState(false)
  return (
    <>
      <Bottone variante="secondario" dimensione="piccolo" onClick={() => setAperta(true)}>
        Configura su client di posta
      </Bottone>
      {aperta && <FinestraProgrammi chiudi={() => setAperta(false)} />}
    </>
  )
}

function FinestraProgrammi({ chiudi }: { chiudi: () => void }) {
  const [dati, setDati] = useState<posta.ProgrammiDiPosta | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [vista, setVista] = useState<'mie' | 'tutte' | null>(null)
  const carica = useCallback(() => {
    posta.programmiDiPosta().then(setDati).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(() => { carica() }, [carica])
  // Chi non usa caselle ma vede le richieste apre direttamente «Tutte le richieste».
  const mostrata = vista ?? (dati && !dati.caselle.length && dati.vede_tutte ? 'tutte' : 'mie')
  // Durante la preparazione lo stato si rilegge da solo: la percentuale avanza mentre la finestra e' aperta.
  const inPreparazione = !!dati?.caselle.some((c) => c.stato === 'preparazione')
  useEffect(() => {
    if (!inPreparazione) return
    const t = window.setInterval(carica, 30_000)
    return () => window.clearInterval(t)
  }, [inPreparazione, carica])

  return (
    <Modale titolo="Configura su client di posta" chiudi={chiudi} larga
            azioni={<Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>}>
      <p>
        Puoi usare le caselle che usi con «legge e scrive» anche da Thunderbird, Outlook o dal telefono, con una
        password tua. Tutto resta sincronizzato con il gestionale: letto e non letto, bandierine, cartelle,
        spostamenti, cestino, posta inviata. Ciò che spedisci dal programma risulta tuo.
      </p>
      <p className="piccolo tenue">
        L’accesso va chiesto e lo approvano la Direzione e l’amministratore di sistema (Honey et Molly): lo storico della casella si copia sul
        server di posta, ed è un costo. Approvato, la copia richiede da qualche minuto a qualche ora; poi qui trovi
        la password e le istruzioni.
      </p>
      {dati?.vede_tutte && (
        <Linguette etichetta="Richieste" attiva={mostrata} cambia={setVista}
                   voci={[{ valore: 'mie', etichetta: 'Le mie caselle' }, { valore: 'tutte', etichetta: 'Tutte le richieste' }]} />
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!dati && !errore && <Attesa />}
      {dati && mostrata === 'mie' && (
        dati.caselle.length === 0
          ? <Vuoto testo="Non usi nessuna casella del server di posta di CIDIS con «legge e scrive»." />
          : dati.caselle.map((c) => <VoceCasella key={c.casella.id} voce={c} server={dati.server} cambiata={carica} />)
      )}
      {dati && mostrata === 'tutte' && <TutteLeRichieste />}
    </Modale>
  )
}

function VoceCasella({ voce, server, cambiata }: { voce: posta.CasellaPerProgramma; server: string; cambiata: () => void }) {
  const [motivo, setMotivo] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [copiata, setCopiata] = useState(false)
  const [revoca, setRevoca] = useState<string | null>(null)
  const puoChiedere = !voce.stato || ['rifiutata', 'scaduta', 'revocata'].includes(voce.stato)

  async function chiedi() {
    setInCorso(true); setErrore(null)
    try {
      await posta.chiediProgramma(voce.casella.id, motivo)
      setMotivo('')
      cambiata()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }
  async function mostraPassword() {
    if (!voce.richiesta) return
    setInCorso(true); setErrore(null); setCopiata(false)
    try {
      const r = await posta.passwordProgramma(voce.richiesta.id)
      setPassword(r.password)
      cambiata()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }
  async function copia() {
    if (!password) return
    try {
      await navigator.clipboard.writeText(password)
      setCopiata(true)
    } catch { setErrore('Copia non riuscita: seleziona la password e copiala a mano.') }
  }
  async function revocaOra() {
    if (!voce.richiesta || revoca === null) return
    setInCorso(true); setErrore(null)
    try {
      await posta.revocaProgramma(voce.richiesta.id, revoca)
      setRevoca(null); setPassword(null)
      cambiata()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <section className="riquadro programma-casella" aria-label={voce.casella.indirizzo}>
      <div className="programma-testata">
        <div>
          <div className="voce-principale">{voce.casella.nome_visualizzato}</div>
          <div className="piccolo tenue">{voce.casella.indirizzo}</div>
        </div>
        {voce.stato && (
          <Etichetta tono={STATI[voce.stato].tono}>{testoStato({ stato: voce.stato, avanzamento: voce.avanzamento })}</Etichetta>
        )}
      </div>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {voce.stato === 'rifiutata' && voce.richiesta?.nota && <p className="piccolo">Motivo del rifiuto: {voce.richiesta.nota}</p>}
      {voce.stato === 'revocata' && voce.richiesta?.motivo_revoca && (
        <p className="piccolo">Revocata{voce.richiesta.revocata_il ? ` il ${giorno.format(new Date(voce.richiesta.revocata_il))}` : ''}:
          {' '}{voce.richiesta.motivo_revoca}</p>
      )}
      {voce.stato === 'in_attesa' && voce.richiesta && (
        <p className="piccolo">
          Chiesta il {giorno.format(new Date(voce.richiesta.richiesta_il))}. Decisioni arrivate: {voce.richiesta.decisioni} di 2
          (Direzione e amministratore di sistema).
        </p>
      )}
      {voce.stato === 'preparazione' && (
        <>
          <progress className="rete-avanzamento" max={100} value={voce.avanzamento ?? 0}
                    aria-label={`Copia dello storico: ${voce.avanzamento ?? 0}%`} />
          <p className="piccolo tenue">
            {voce.attesa_spazio
              ? 'La copia aspetta spazio sul server di posta: l’assistenza lo sta allargando.'
              : 'Lo storico della casella si sta copiando sul server di posta. Quando è finito trovi qui la password.'}
          </p>
        </>
      )}

      {puoChiedere && voce.stima && (
        <>
          <Avviso tipo="attenzione">
            {voce.stima.gia_sincronizzata
              ? 'La casella è già sul server di posta per un’altra persona: lo spazio non cresce.'
              : `Costo per CIDIS: lo storico di questa casella (${voce.stima.messaggi} messaggi, circa `
                + `${numero.format(voce.stima.gb)} GB) va copiato sul server di posta, circa `
                + `${euro.format(voce.stima.euro_mese)} al mese di spazio. Chiedi solo se ti serve davvero.`}
          </Avviso>
          <AreaTesto etichetta="Motivo" valore={motivo} cambia={setMotivo} righe={2} maxLength={1000}
                     segnaposto="Per esempio: lavoro fuori sede e leggo la posta dal telefono" />
          <Bottone variante="primario" inCorso={inCorso} disabled={motivo.trim().length < 10} onClick={() => void chiedi()}>
            Chiedi l’accesso da un programma di posta
          </Bottone>
        </>
      )}

      {voce.stato === 'pronta' && (
        <>
          {password ? (
            <Avviso tipo="successo">
              <p>La tua password per {voce.casella.indirizzo}: la vedi solo adesso. Copiala nel programma.</p>
              <p><code className="programma-password">{password}</code></p>
              <Bottone dimensione="piccolo" onClick={() => void copia()}>{copiata ? 'Copiata' : 'Copia'}</Bottone>
            </Avviso>
          ) : voce.credenziale ? (
            <p className="piccolo">
              Password data il {giorno.format(new Date(voce.credenziale.creata_il))}
              {voce.credenziale.primo_accesso_il
                ? `; primo accesso dal programma il ${quando.format(new Date(voce.credenziale.primo_accesso_il))}`
                : '; nessun accesso dal programma finora'}
              {voce.credenziale.ultimo_uso_il ? `; ultimo invio il ${quando.format(new Date(voce.credenziale.ultimo_uso_il))}` : ''}.
              {voce.credenziale.origine === 'script' && ' È la password data dall’assistenza: «Nuova password» la sostituisce.'}
            </p>
          ) : (
            <p>La casella è pronta. La password si vede una volta sola: tienila a portata di mano mentre configuri il programma.</p>
          )}
          <div className="programma-comandi">
            <Bottone variante={voce.credenziale ? 'secondario' : 'primario'} inCorso={inCorso} onClick={() => void mostraPassword()}>
              {voce.credenziale ? 'Nuova password' : 'Mostra la password'}
            </Bottone>
            <Bottone variante="discreto" onClick={() => setRevoca('')}>Revoca</Bottone>
          </div>
          {voce.credenziale && !password && (
            <p className="piccolo tenue">
              «Nuova password» sostituisce quella di prima: il programma la chiederà di nuovo.
            </p>
          )}
          <Istruzioni server={server} indirizzo={voce.casella.indirizzo} />
        </>
      )}
      {voce.stato && ['in_attesa', 'preparazione'].includes(voce.stato) && (
        <Bottone variante="discreto" dimensione="piccolo" onClick={() => setRevoca('')}>Ritira la richiesta</Bottone>
      )}
      {revoca !== null && (
        <div className="programma-revoca">
          <AreaTesto etichetta="Motivo della revoca" valore={revoca} cambia={setRevoca} righe={2} maxLength={500} />
          <div className="programma-comandi">
            <Bottone variante="discreto" onClick={() => setRevoca(null)}>Annulla</Bottone>
            <Bottone variante="critico" inCorso={inCorso} disabled={revoca.trim().length < 3} onClick={() => void revocaOra()}>
              Revoca
            </Bottone>
          </div>
          <p className="piccolo tenue">Il programma non entra più entro qualche minuto. La posta resta nel gestionale.</p>
        </div>
      )}
    </section>
  )
}

type Programma = 'thunderbird' | 'outlook' | 'apple' | 'android'

/** Le istruzioni passo passo, con i dati della casella. */
export function Istruzioni({ server, indirizzo }: { server: string; indirizzo: string }) {
  const [quale, setQuale] = useState<Programma>('thunderbird')
  const dati = (
    <ul className="programma-dati">
      <li>Nome utente: <strong>{indirizzo}</strong> (lo stesso in entrata e in uscita)</li>
      <li>Password: quella che hai copiato</li>
      <li>Posta in entrata (IMAP): <strong>{server}</strong>, porta <strong>993</strong>, <strong>SSL/TLS</strong>, password normale</li>
      <li>Posta in uscita (SMTP): <strong>{server}</strong>, porta <strong>465</strong>, <strong>SSL/TLS</strong>, password normale</li>
    </ul>
  )
  return (
    <div className="programma-istruzioni">
      <div className="posta-titoletto">Istruzioni</div>
      {dati}
      <Linguette etichetta="Programma" attiva={quale} cambia={setQuale} voci={[
        { valore: 'thunderbird', etichetta: 'Thunderbird' }, { valore: 'outlook', etichetta: 'Outlook' },
        { valore: 'apple', etichetta: 'Apple Mail e iPhone' }, { valore: 'android', etichetta: 'Android' },
      ]} />
      {quale === 'thunderbird' && (
        <ol>
          <li>Menu ☰ → Impostazioni account → Azioni account → «Aggiungi account di posta».</li>
          <li>Scrivi nome, indirizzo {indirizzo} e la password, poi «Configura manualmente».</li>
          <li>In entrata: IMAP, server {server}, porta 993, SSL/TLS, «Password normale», nome utente {indirizzo}.</li>
          <li>In uscita: SMTP, server {server}, porta 465, SSL/TLS, «Password normale», nome utente {indirizzo}.</li>
          <li>«Fatto». In «Copie e cartelle» la posta inviata va in «Sent» (Thunderbird la sceglie da solo).</li>
        </ol>
      )}
      {quale === 'outlook' && (
        <>
          <p className="piccolo"><strong>Outlook classico (Windows)</strong></p>
          <ol>
            <li>File → Aggiungi account. Scrivi {indirizzo}, apri «Opzioni avanzate», spunta «Configura manualmente
              l’account», poi «Connetti» e scegli IMAP.</li>
            <li>Posta in arrivo: {server}, porta 993, crittografia SSL/TLS. Posta in uscita: {server}, porta 465,
              crittografia SSL/TLS. Avanti, poi la password.</li>
            <li>Se chiede le cartelle, la posta inviata è «Sent» e il cestino «Trash».</li>
          </ol>
          <p className="piccolo"><strong>Nuovo Outlook (Windows) e Outlook per Mac</strong></p>
          <ol>
            <li>Impostazioni → Account → Account di posta → «Aggiungi account». Scrivi {indirizzo}.</li>
            <li>Quando chiede il tipo, scegli IMAP e compila i server come sopra (993 e 465, SSL/TLS), con lo stesso
              nome utente e la stessa password in entrata e in uscita.</li>
          </ol>
        </>
      )}
      {quale === 'apple' && (
        <>
          <p className="piccolo"><strong>iPhone e iPad</strong></p>
          <ol>
            <li>Impostazioni → App → Mail → Account → «Aggiungi account» → «Altro» → «Aggiungi account Mail».</li>
            <li>Nome, {indirizzo}, password, descrizione. Avanti, scegli IMAP.</li>
            <li>Server posta in arrivo: {server}, nome utente {indirizzo}. Server posta in uscita: {server}, nome
              utente {indirizzo}, la stessa password. Avanti.</li>
            <li>Se non si collega: Account → l’account → Server posta in uscita → {server}: SSL attivo, porta 465.
              Avanzate: SSL attivo, porta 993.</li>
          </ol>
          <p className="piccolo"><strong>Mail sul Mac</strong></p>
          <ol>
            <li>Mail → Aggiungi account → «Altro account Mail…». Nome, {indirizzo}, password.</li>
            <li>Se chiede i server: IMAP, in entrata e in uscita {server}, nome utente {indirizzo}. Mail sceglie da
              solo SSL e le porte 993 e 465.</li>
          </ol>
        </>
      )}
      {quale === 'android' && (
        <ol>
          <li>Apri Gmail → foto del profilo → «Aggiungi un altro account» → «Altro».</li>
          <li>Scrivi {indirizzo}, poi «Configurazione manuale» → «Personale (IMAP)». La password.</li>
          <li>Server in entrata: {server}, tipo di sicurezza SSL/TLS, porta 993.</li>
          <li>Server in uscita: {server}, SSL/TLS, porta 465, «Richiedi accesso» attivo, stesso nome utente e
            password.</li>
          <li>Con un’altra app di posta (Samsung Email, K-9 o Thunderbird per Android) i dati sono gli stessi.</li>
        </ol>
      )}
      <p className="piccolo tenue">
        Se la password non viene accettata, controlla che il nome utente sia l’indirizzo completo. Dopo tre tentativi
        sbagliati il server rallenta per qualche minuto.
      </p>
    </div>
  )
}

function TutteLeRichieste() {
  const [dati, setDati] = useState<{ puo_revocare: boolean; richieste: posta.RichiestaProgramma[] } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [daRevocare, setDaRevocare] = useState<posta.RichiestaProgramma | null>(null)
  const [motivo, setMotivo] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const carica = useCallback(() => {
    posta.tutteLeRichiesteProgramma().then(setDati).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(() => { carica() }, [carica])

  async function revoca() {
    if (!daRevocare) return
    setInCorso(true); setErrore(null)
    try {
      await posta.revocaProgramma(daRevocare.id, motivo)
      setDaRevocare(null); setMotivo('')
      carica()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  if (errore && !dati) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa />
  return (
    <>
      <p className="piccolo tenue">
        Le richieste in attesa si decidono in <Link to="/approvazioni">Approvazioni</Link>: servono la Direzione e l’amministratore di sistema (Honey et Molly).
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Tabella intestazioni={['Casella', 'Persona', 'Stato', 'Spazio stimato', <span className="solo-lettore" key="a">Azioni</span>]}
               vuoto={<Vuoto testo="Nessuna richiesta." />}>
        {dati.richieste.map((r) => (
          <tr key={r.id}>
            <td data-etichetta="Casella">{r.casella.indirizzo}</td>
            <td data-etichetta="Persona">{r.persona.cognome} {r.persona.nome}</td>
            <td data-etichetta="Stato">
              <Etichetta tono={STATI[r.stato].tono}>{testoStato(r)}</Etichetta>
              {r.stato === 'in_attesa' && <div className="piccolo tenue">{r.decisioni} di 2 decisioni</div>}
              {r.credenziale?.primo_accesso_il && (
                <div className="piccolo tenue">primo accesso {quando.format(new Date(r.credenziale.primo_accesso_il))}</div>
              )}
            </td>
            <td data-etichetta="Spazio stimato" className="piccolo">{numero.format(r.stima_byte / 1e9)} GB</td>
            <td data-etichetta="Azioni">
              {dati.puo_revocare && ['in_attesa', 'preparazione', 'pronta'].includes(r.stato) && (
                <Bottone dimensione="piccolo" variante="discreto" onClick={() => { setMotivo(''); setDaRevocare(r) }}
                         aria-label={`Revoca il programma di ${r.persona.nome} ${r.persona.cognome} su ${r.casella.indirizzo}`}>
                  Revoca
                </Bottone>
              )}
            </td>
          </tr>
        ))}
      </Tabella>
      {daRevocare && (
        <div className="programma-revoca">
          <p>
            {daRevocare.persona.nome} {daRevocare.persona.cognome} non userà più {daRevocare.casella.indirizzo} dal
            programma di posta: entro qualche minuto il server rifiuta la sua password.
          </p>
          <AreaTesto etichetta="Motivo della revoca" valore={motivo} cambia={setMotivo} righe={2} maxLength={500} />
          <div className="programma-comandi">
            <Bottone variante="discreto" onClick={() => setDaRevocare(null)}>Annulla</Bottone>
            <Bottone variante="critico" inCorso={inCorso} disabled={motivo.trim().length < 3} onClick={() => void revoca()}>
              Revoca
            </Bottone>
          </div>
        </div>
      )}
    </>
  )
}
